import React, { useState, useEffect } from "react";
import * as api from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { ShoppingCart, ArrowDownToLine, SlidersHorizontal } from "lucide-react";
import { MOVEMENT_KINDS, Field, SELECT_CLS } from "./constants";
import { CompanySelect } from "./PrinterForm";

const KIND_ICONS = { purchase: ShoppingCart, consumption: ArrowDownToLine, adjustment: SlidersHorizontal };
const today = () => new Date().toISOString().slice(0, 10);

export function MovementDialog({ consumable, printers = [], companies = [], kind: initialKind = "purchase", defaultPrinterId = "", onClose, onDone }) {
    const open = !!consumable;
    const [kind, setKind] = useState(initialKind);
    const [form, setForm] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setKind(initialKind);
        setForm({
            quantity: "1", new_quantity: String(consumable.quantity ?? 0), date: today(),
            printer_id: defaultPrinterId || (consumable.compatible_printer_ids?.length === 1 ? consumable.compatible_printer_ids[0] : ""),
            unit_price: consumable.unit_price ?? "", company_id: consumable.company_id || "", invoice_number: "", notes: "",
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, consumable?.id, initialKind, defaultPrinterId]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
    const compatible = printers.filter((p) => (consumable?.compatible_printer_ids || []).includes(p.id));
    const others = printers.filter((p) => !(consumable?.compatible_printer_ids || []).includes(p.id));

    const submit = async () => {
        setSaving(true);
        try {
            const payload = { kind, date: form.date, notes: form.notes };
            if (kind === "adjustment") payload.new_quantity = Number(form.new_quantity);
            else payload.quantity = Number(form.quantity);
            if (kind === "consumption") payload.printer_id = form.printer_id || null;
            if (kind === "purchase") Object.assign(payload, { unit_price: form.unit_price === "" ? null : Number(form.unit_price), company_id: form.company_id || null, invoice_number: form.invoice_number });
            const res = await api.addConsumableMovement(consumable.id, payload);
            toast.success(`${MOVEMENT_KINDS[kind].label} enregistré — stock : ${res.new_quantity}`);
            onDone?.(res);
            onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
        finally { setSaving(false); }
    };

    const valid = kind === "adjustment" ? form.new_quantity !== "" && Number(form.new_quantity) >= 0 : Number(form.quantity) > 0;

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-md">
                <DialogHeader><DialogTitle>Mouvement de stock — {consumable?.name}</DialogTitle></DialogHeader>
                <p className="text-xs text-gray-500 -mt-2">Stock actuel : <span className="font-semibold text-gray-800" data-testid="mv-current-stock">{consumable?.quantity ?? 0}</span>{consumable?.reference ? ` · Réf. ${consumable.reference}` : ""}</p>
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1" data-testid="mv-kind-toggle">
                    {Object.entries(MOVEMENT_KINDS).map(([k, v]) => {
                        const Icon = KIND_ICONS[k];
                        return (
                            <button key={k} onClick={() => setKind(k)} data-testid={`mv-kind-${k}`}
                                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium rounded-md transition-colors ${kind === k ? "bg-white text-teal-700 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                                <Icon className="w-3.5 h-3.5" />{v.label}
                            </button>
                        );
                    })}
                </div>
                <div className="grid grid-cols-2 gap-3 py-1">
                    {kind === "adjustment" ? (
                        <Field label="Nouvelle quantité en stock"><Input type="number" min="0" value={form.new_quantity || ""} onChange={set("new_quantity")} data-testid="mv-new-quantity" /></Field>
                    ) : (
                        <Field label="Quantité"><Input type="number" min="1" value={form.quantity || ""} onChange={set("quantity")} data-testid="mv-quantity" /></Field>
                    )}
                    <Field label="Date"><Input type="date" value={form.date || ""} onChange={set("date")} data-testid="mv-date" /></Field>
                    {kind === "consumption" && (
                        <Field label="Installé sur l'imprimante" className="col-span-2">
                            <select value={form.printer_id || ""} onChange={set("printer_id")} className={SELECT_CLS} data-testid="mv-printer">
                                <option value="">— Non précisé —</option>
                                {compatible.length > 0 && <optgroup label="Compatibles">{compatible.map((p) => <option key={p.id} value={p.id}>{p.name}{p.site_name ? ` · ${p.site_name}` : ""}</option>)}</optgroup>}
                                {others.length > 0 && <optgroup label="Autres imprimantes">{others.map((p) => <option key={p.id} value={p.id}>{p.name}{p.site_name ? ` · ${p.site_name}` : ""}</option>)}</optgroup>}
                            </select>
                        </Field>
                    )}
                    {kind === "purchase" && (
                        <>
                            <Field label="Prix unitaire"><Input type="number" step="0.01" value={form.unit_price ?? ""} onChange={set("unit_price")} data-testid="mv-unit-price" /></Field>
                            <Field label="N° facture"><Input value={form.invoice_number || ""} onChange={set("invoice_number")} data-testid="mv-invoice" /></Field>
                            <Field label="Fournisseur" className="col-span-2"><CompanySelect value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} companies={companies} testId="mv-company" /></Field>
                        </>
                    )}
                    <Field label="Remarques" className="col-span-2"><Input value={form.notes || ""} onChange={set("notes")} placeholder="optionnel" data-testid="mv-notes" /></Field>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Annuler</Button>
                    <Button onClick={submit} disabled={saving || !valid} data-testid="mv-submit">{saving ? "..." : "Enregistrer"}</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
